import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
   GetProgramsQueryParams,
   createProgram,
   deleteProgram,
   getPrograms,
   reorderPrograms,
   updateProgram,
   upsertUserProgram,
} from 'src/api/programApi';
import { showToast } from 'src/components/toast';

export const ProgramQueryKeys = {
   GetPrograms: 'getPrograms',
   CreatProgram: 'createProgram',
   UpdateProgram: 'updateProgram',
   DeleteProgram: 'deleteProgram',
   UpsertUserProgram: 'upsertUserProgram',
};

export type MutationParams<TData = any, TError = any, TVariables = any> = {
   onSuccessCallBack?: (data?: TData) => void;
   onErrorCallBack?: (error?: TError) => void;
   onMutateCallBack?: (variables?: TVariables) => void;
   onSettledCallBack?: (data?: TData, error?: TError) => void;
};

export const useGetProgramsByOrgId = (query: GetProgramsQueryParams) => {
   return useQuery({
      queryKey: [ProgramQueryKeys.GetPrograms, query],
      queryFn: () => getPrograms(query),
      staleTime: Infinity,
      retry: 0,
      enabled: !!query.organizationId,
   });
};

export const useGetPrograms = (query: GetProgramsQueryParams) => {
   return useQuery({
      queryKey: [ProgramQueryKeys.GetPrograms, query],
      queryFn: () => getPrograms(query),
      staleTime: Infinity,
      retry: 0,
   });
};

export const useCreateProgram = (callBack: MutationParams) => {
   const { onSuccessCallBack, onErrorCallBack, onMutateCallBack, onSettledCallBack } = callBack;
   const queryClient = useQueryClient();

   return useMutation({
      mutationKey: [ProgramQueryKeys.CreatProgram],
      mutationFn: createProgram,
      onError: (err: any) => {
         const { errors } = err.response?.data || [];

         if (errors && errors.length > 0) {
            if (errors[0]?.detail?.includes('Duplicate')) {
               showToast('error', 'Index already exists. Use another one');
            } else {
               showToast('error', errors[0]?.detail);
            }
         } else {
            showToast('error', 'Error while adding new program');
         }

         onErrorCallBack && onErrorCallBack();
      },
      onSuccess: async () => {
         await queryClient.invalidateQueries({ queryKey: [ProgramQueryKeys.GetPrograms] });
         showToast('success', 'Successfully added new program');

         onSuccessCallBack && onSuccessCallBack();
      },
      onMutate: () => onMutateCallBack && onMutateCallBack(),
      onSettled: () => onSettledCallBack && onSettledCallBack(),
   });
};

export const useUpdateProgram = (callBack: MutationParams) => {
   const { onSuccessCallBack, onErrorCallBack, onMutateCallBack, onSettledCallBack } = callBack;
   const queryClient = useQueryClient();

   return useMutation({
      mutationKey: [ProgramQueryKeys.UpdateProgram],
      mutationFn: updateProgram,
      onError: () => {
         showToast('error', 'Error while updating program');

         onErrorCallBack && onErrorCallBack();
      },
      onSuccess: async () => {
         await queryClient.invalidateQueries({ queryKey: [ProgramQueryKeys.GetPrograms] });

         onSuccessCallBack && onSuccessCallBack();
      },
      onMutate: () => onMutateCallBack && onMutateCallBack(),
      onSettled: () => onSettledCallBack && onSettledCallBack(),
   });
};

export const useDeleteProgram = (callBack: MutationParams) => {
   const { onSuccessCallBack, onErrorCallBack, onMutateCallBack, onSettledCallBack } = callBack;
   const queryClient = useQueryClient();

   return useMutation({
      mutationKey: [ProgramQueryKeys.DeleteProgram],
      mutationFn: deleteProgram,
      onError: () => {
         showToast('error', 'Error while deleting program');

         onErrorCallBack && onErrorCallBack();
      },
      onSuccess: async () => {
         await queryClient.invalidateQueries({ queryKey: [ProgramQueryKeys.GetPrograms] });
         showToast('success', 'Successfully deleted program');

         onSuccessCallBack && onSuccessCallBack();
      },
      onMutate: () => onMutateCallBack && onMutateCallBack(),
      onSettled: () => onSettledCallBack && onSettledCallBack(),
   });
};

export const useUpsertUserProgram = (callBack: MutationParams) => {
   const { onSuccessCallBack, onErrorCallBack, onMutateCallBack, onSettledCallBack } = callBack;
   const queryClient = useQueryClient();

   return useMutation({
      mutationKey: [ProgramQueryKeys.UpsertUserProgram],
      mutationFn: upsertUserProgram,
      onError: () => {
         showToast('error', 'Error while upsert user-program');

         onErrorCallBack && onErrorCallBack();
      },
      onSuccess: async () => {
         await queryClient.invalidateQueries({ queryKey: [ProgramQueryKeys.GetPrograms] });

         onSuccessCallBack && onSuccessCallBack();
      },
      onMutate: () => onMutateCallBack && onMutateCallBack(),
      onSettled: () => onSettledCallBack && onSettledCallBack(),
   });
};

export const useReorderPrograms = (callBack: MutationParams) => {
   const { onSuccessCallBack, onErrorCallBack, onMutateCallBack, onSettledCallBack } = callBack;
   const queryClient = useQueryClient();

   return useMutation({
      mutationKey: [ProgramQueryKeys.UpdateProgram],
      mutationFn: reorderPrograms,
      onError: () => {
         showToast('error', 'Error while updating programs');

         onErrorCallBack && onErrorCallBack();
      },
      onSuccess: async () => {
         await queryClient.invalidateQueries({ queryKey: [ProgramQueryKeys.GetPrograms] });

         onSuccessCallBack && onSuccessCallBack();
      },
      onMutate: () => onMutateCallBack && onMutateCallBack(),
      onSettled: () => onSettledCallBack && onSettledCallBack(),
   });
};
